import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faEdit, faSave, faMapMarkerAlt,
  faLink, faCheckCircle,
  faUsers, faInfoCircle, faSpinner,
  faHistory, faGlobe, faCoins, faExchangeAlt, faUserTie, faTrash,
  faSitemap
} from '@fortawesome/free-solid-svg-icons';
import EntityAccessPanel from './EntityAccessPanel';
import LinkEntityModal from '../modals/LinkEntityModal';
import TradeOpsDeleteModal from '../../TradeOpsDeleteModal';
import { fetchEntity, updateEntity, fetchAccessAudit, hardDeleteEntity } from '../../../services/corporateStructureService';
import { useAuth } from '../../../context/AuthContext';
import './EntityDetailPanel.css';

const EntityDetailPanel = ({ entity, groupId, existingEntities = [], onClose, onEntityUpdated }) => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_superuser;

  const [fullEntity, setFullEntity] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditLoaded, setAuditLoaded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteWarning, setDeleteWarning] = useState(false);
  const [deleteWarningMsg, setDeleteWarningMsg] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [saveError, setSaveError] = useState('');

  const loadEntity = useCallback(async () => {
    if (!entity?.id) return;
    try {
      setLoading(true);
      const data = await fetchEntity(entity.id);
      setFullEntity(data);
      setEditData(data);
    } catch (err) {
      console.error('Failed to load entity:', err);
    } finally {
      setLoading(false);
    }
  }, [entity?.id]);

  const loadAudit = useCallback(async () => {
    if (!entity?.id || auditLoaded) return;
    try {
      setAuditLoading(true);
      const data = await fetchAccessAudit(entity.id);
      setAuditLogs(data);
      setAuditLoaded(true);
    } catch (err) {
      console.error('Failed to load audit log:', err);
    } finally {
      setAuditLoading(false);
    }
  }, [entity?.id, auditLoaded]);

  useEffect(() => {
    loadEntity();
  }, [loadEntity]);

  useEffect(() => {
    if (activeTab === 'audit') {
      loadAudit();
    }
  }, [activeTab, loadAudit]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError('');
      await updateEntity(entity.id, {
        entity_name: editData.entity_name,
        trading_short_name: editData.trading_short_name,
        shareholder_note: editData.shareholder_note,
        country: editData.country,
        country_code: editData.country_code,
        base_currency: editData.base_currency,
        currency_conversion_note: editData.currency_conversion_note,
        in_consolidation_circle: editData.in_consolidation_circle,
        abacus_client_number: (editData.abacus_client_number || '').trim(),
        node_color: editData.node_color,
        group: groupId,
        parent_entity: editData.parent_entity,
      });
      setEditing(false);
      loadEntity();
      onEntityUpdated();
    } catch (err) {
      console.error('Failed to save entity:', err);
      const data = err.response?.data;
      const msg =
        data?.parent_entity?.[0] ||
        data?.abacus_client_number?.[0] ||
        data?.entity_name?.[0] ||
        data?.non_field_errors?.[0] ||
        data?.detail ||
        'Failed to save entity.';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleLinkSaved = () => {
    setShowLinkModal(null);
    loadEntity();
    onEntityUpdated();
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await hardDeleteEntity(entity.id);
      setShowDeleteModal(false);
      onClose();
      onEntityUpdated();
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.blockers) {
        const lines = data.blockers.map(b => {
          const detail = b.items?.length ? `: ${b.items.join(', ')}` : '';
          return `${b.type} (${b.count})${detail}`;
        });
        setDeleteWarningMsg(
          `${data.message}\n\nConnected data:\n${lines.join('\n')}`
        );
        setDeleteWarning(true);
      } else {
        setDeleteWarningMsg(data?.message || data?.error || 'An unexpected error occurred.');
        setDeleteWarning(true);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const displayEntity = fullEntity || entity;

  const parentOptions = React.useMemo(() => {
    if (!entity?.id || !existingEntities.length) return [];
    const childIds = new Set();
    const collectDescendants = (nodeId) => {
      existingEntities.forEach(e => {
        if (String(e.parent_entity) === String(nodeId) && !childIds.has(e.id)) {
          childIds.add(e.id);
          collectDescendants(e.id);
        }
      });
    };
    collectDescendants(entity.id);
    return existingEntities.filter(e => e.id !== entity.id && !childIds.has(e.id));
  }, [entity?.id, existingEntities]);

  if (loading && !fullEntity) {
    return (
      <div className="edp-panel">
        <div className="edp-header">
          <span>Loading...</span>
          <button className="edp-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="edp-loading">
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      </div>
    );
  }

  const TABS = [
    { key: 'info', label: 'Info', icon: faInfoCircle },
    { key: 'access', label: 'Access', icon: faUsers },
    { key: 'audit', label: 'Audit Logs', icon: faHistory },
  ];

  return (
    <div className="edp-panel">
      {/* Header */}
      <div className="edp-header">
        <div className="edp-header-title">
          <div className="edp-color-dot" style={{ backgroundColor: displayEntity.node_color || '#4a6b25' }} />
          <div className="edp-header-text">
            <h3>{displayEntity.entity_name}</h3>
            {displayEntity.trading_short_name && (
              <span className="edp-header-short">{displayEntity.trading_short_name}</span>
            )}
          </div>
          {displayEntity.in_consolidation_circle && (
            <span className="edp-consol-header-badge">
              <FontAwesomeIcon icon={faCheckCircle} /> Consolidation
            </span>
          )}
        </div>
        <div className="edp-header-actions">
          {activeTab === 'info' && (
            editing ? (
              <button className="sg-btn sg-btn-primary" onClick={handleSave} disabled={saving}>
                <FontAwesomeIcon icon={saving ? faSpinner : faSave} spin={saving} />
                Save
              </button>
            ) : (
              <>
                <button className="sg-btn sg-btn-secondary" onClick={() => { setSaveError(''); setEditing(true); }}>
                  <FontAwesomeIcon icon={faEdit} />
                  Edit
                </button>
                {isAdmin && (
                  <button
                    className="sg-btn sg-btn-danger"
                    onClick={() => { setDeleteWarning(false); setDeleteWarningMsg(''); setShowDeleteModal(true); }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                    Delete
                  </button>
                )}
              </>
            )
          )}
          <button className="edp-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="edp-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`edp-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <FontAwesomeIcon icon={tab.icon} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="edp-body">
        {activeTab === 'info' && (
          <div className="edp-tab-content">
            {/* Location & Currency Card */}
            <div className="edp-card">
              <div className="edp-card-header">
                <FontAwesomeIcon icon={faGlobe} className="edp-card-header-icon" />
                <span>Location & Currency</span>
              </div>
              {editing ? (
                <div className="edp-edit-grid">
                  {saveError && (
                    <div className="sg-modal-error">{saveError}</div>
                  )}
                  <div className="edp-parent-field">
                    <span className="edp-parent-field-label">
                      <FontAwesomeIcon icon={faSitemap} />
                      Parent Entity
                    </span>
                    <div className="edp-select-wrapper">
                      <select
                        className={`edp-edit-select ${!editData.parent_entity ? 'is-empty' : ''}`}
                        value={editData.parent_entity || ''}
                        disabled={parentOptions.length === 0}
                        onChange={e => setEditData({
                          ...editData,
                          parent_entity: e.target.value ? Number(e.target.value) : null,
                        })}
                      >
                        <option value="">
                          {parentOptions.length === 0
                            ? 'No other entities available'
                            : 'None  —  Top-level entity'}
                        </option>
                        {parentOptions.map(p => (
                          <option key={p.id} value={p.id}>{p.entity_name}</option>
                        ))}
                      </select>
                    </div>
                    <span className="edp-parent-field-hint">
                      Entities that are descendants of this one are hidden to prevent cycles.
                    </span>
                  </div>
                  <div className="edp-edit-row">
                    <label className="edp-edit-label">
                      Country
                      <input className="edp-edit-input" value={editData.country || ''} onChange={e => setEditData({...editData, country: e.target.value})} />
                    </label>
                    <label className="edp-edit-label">
                      Code
                      <input className="edp-edit-input" value={editData.country_code || ''} onChange={e => setEditData({...editData, country_code: e.target.value})} maxLength={3} />
                    </label>
                  </div>
                  <div className="edp-edit-row">
                    <label className="edp-edit-label">
                      Base Currency
                      <input className="edp-edit-input" value={editData.base_currency || ''} onChange={e => setEditData({...editData, base_currency: e.target.value})} maxLength={3} />
                    </label>
                    <label className="edp-edit-label">
                      Node Color
                      <input type="color" className="edp-edit-input edp-color-input" value={editData.node_color || '#4a6b25'} onChange={e => setEditData({...editData, node_color: e.target.value})} />
                    </label>
                  </div>
                  <label className="edp-edit-label">
                    Currency Conversion Note
                    <input className="edp-edit-input" value={editData.currency_conversion_note || ''} onChange={e => setEditData({...editData, currency_conversion_note: e.target.value})} />
                  </label>
                  <label className="edp-edit-label">
                    Shareholder Note
                    <input className="edp-edit-input" value={editData.shareholder_note || ''} onChange={e => setEditData({...editData, shareholder_note: e.target.value})} />
                  </label>
                  <label className="edp-edit-label">
                    Abacus books number
                    <input className="edp-edit-input" inputMode="numeric" placeholder="e.g. 100, from OBT" value={editData.abacus_client_number || ''} onChange={e => setEditData({...editData, abacus_client_number: e.target.value})} maxLength={10} />
                    <span className="edp-muted" style={{ fontSize: 12 }}>The number OBT gave this company's books in Abacus. Leave empty if it doesn't book in Abacus: its invoices then never go there.</span>
                  </label>
                  <label className={`edp-consol-toggle ${editData.in_consolidation_circle ? 'active' : ''}`}>
                    <input
                      type="checkbox"
                      checked={editData.in_consolidation_circle || false}
                      onChange={e => setEditData({...editData, in_consolidation_circle: e.target.checked})}
                    />
                    <FontAwesomeIcon icon={faCheckCircle} />
                    Consolidation Circle
                  </label>
                </div>
              ) : (
                <div className="edp-detail-grid">
                  <div className="edp-detail-item">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="edp-detail-icon" />
                    <div className="edp-detail-text">
                      <span className="edp-detail-label">Country</span>
                      <span className="edp-detail-value">{displayEntity.country} <span className="edp-muted">({displayEntity.country_code})</span></span>
                    </div>
                  </div>
                  <div className="edp-detail-item">
                    <FontAwesomeIcon icon={faCoins} className="edp-detail-icon" />
                    <div className="edp-detail-text">
                      <span className="edp-detail-label">Base Currency</span>
                      <span className="edp-detail-value">
                        <span className="edp-currency-badge">{displayEntity.base_currency}</span>
                      </span>
                    </div>
                  </div>
                  <div className="edp-detail-item">
                    <FontAwesomeIcon icon={faExchangeAlt} className="edp-detail-icon" />
                    <div className="edp-detail-text">
                      <span className="edp-detail-label">Abacus books</span>
                      <span className="edp-detail-value">
                        {displayEntity.abacus_client_number ? displayEntity.abacus_client_number : <span className="edp-muted">Doesn't send to Abacus</span>}
                      </span>
                    </div>
                  </div>
                  {displayEntity.currency_conversion_note && (
                    <div className="edp-detail-item edp-detail-full">
                      <FontAwesomeIcon icon={faExchangeAlt} className="edp-detail-icon" />
                      <div className="edp-detail-text">
                        <span className="edp-detail-label">Conversion</span>
                        <span className="edp-detail-value edp-detail-note">{displayEntity.currency_conversion_note}</span>
                      </div>
                    </div>
                  )}
                  {displayEntity.shareholder_note && (
                    <div className="edp-detail-item edp-detail-full">
                      <FontAwesomeIcon icon={faUserTie} className="edp-detail-icon" />
                      <div className="edp-detail-text">
                        <span className="edp-detail-label">Shareholder</span>
                        <span className="edp-detail-value">{displayEntity.shareholder_note}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Linked Systems Card */}
            {fullEntity && (
              <div className="edp-card">
                <div className="edp-card-header">
                  <FontAwesomeIcon icon={faLink} className="edp-card-header-icon" />
                  <span>Linked Systems</span>
                </div>
                <div className="edp-links-list">
                  {[
                    { key: 'hms', label: 'Entity', value: fullEntity.linked_hms_entity_name },
                    { key: 'crm', label: 'CRM Profile', value: fullEntity.linked_crm_profile_name },
                    { key: 'office', label: 'Office', value: fullEntity.linked_office_name },
                  ].map(link => (
                    <div key={link.key} className="edp-link-item">
                      <div className="edp-link-info">
                        <span className="edp-link-label">{link.label}</span>
                        {link.value ? (
                          <span className="edp-link-value-active">{link.value}</span>
                        ) : (
                          <span className="edp-link-value-empty">Not linked</span>
                        )}
                      </div>
                      <button className="edp-link-btn" onClick={() => setShowLinkModal(link.key)}>
                        <FontAwesomeIcon icon={faLink} />
                        {link.value ? 'Change' : 'Link'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'access' && entity?.id && (
          <div className="edp-tab-content">
            <EntityAccessPanel entityId={entity.id} entityName={displayEntity.entity_name} />
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="edp-tab-content">
            {auditLoading ? (
              <div className="edp-loading">
                <FontAwesomeIcon icon={faSpinner} spin />
              </div>
            ) : auditLogs.length === 0 ? (
              <div className="edp-empty-state">
                <FontAwesomeIcon icon={faHistory} className="edp-empty-icon" />
                <span>No audit entries yet</span>
              </div>
            ) : (
              <div className="edp-audit-list">
                {auditLogs.map(log => (
                  <div key={log.id} className="edp-audit-entry">
                    <span className={`edp-audit-badge edp-audit-${log.action}`}>
                      {log.action}
                    </span>
                    <div className="edp-audit-detail">
                      <span className="edp-audit-who">{log.employee_name}</span>
                      <span className="edp-audit-by">by {log.performed_by_name}</span>
                    </div>
                    <span className="edp-audit-time">
                      {new Date(log.performed_at).toLocaleDateString()} {new Date(log.performed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showLinkModal && (
        <LinkEntityModal
          entityId={entity.id}
          linkType={showLinkModal}
          currentEntity={fullEntity}
          onClose={() => setShowLinkModal(null)}
          onSaved={handleLinkSaved}
        />
      )}

      <TradeOpsDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        itemType="entity"
        itemDetails={{
          entityName: displayEntity.entity_name,
          country: displayEntity.country ? `${displayEntity.country} (${displayEntity.country_code})` : undefined,
          currency: displayEntity.base_currency,
        }}
        isWarning={deleteWarning}
        warningMessage={deleteWarningMsg}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default EntityDetailPanel;
